// Big clients — the big-fish worklist. Alerts' second lane.
//
// Same `attention_flags` store as the Worklist tab, narrowed to `reason =
// 'big_client'` (a committed budget at or above the threshold) and given the two
// facts that decide whether to act: how much they committed, and whether a human
// has already taken them over.
//
// It is a LIST, not a workbench. Every row opens the project page, which is where
// the conversation, the brief, the matches, and the two controls (Pause agent,
// Stop and notify client) live — one place to act on a project, reached the same
// way from here, from the Alerts worklist, and from a Slack alert's deep link.
// This page's only write is Dismiss, which resolves the flag and deliberately
// changes no automation.
import {
  Badge,
  Button,
  DateRangeFilter,
  EMPTY_DATE_RANGE,
  Input,
  isDateRangeActive,
  SortableTh,
  TableExport,
  TableFilters,
  type DateRange,
  type FilterColumn,
  useColumnFilters,
  useRestoredState,
  useTableSort,
  useToast,
} from "@recruiter/shared";
import { useInfiniteQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useMemo } from "react";

import { AreaBoundary } from "../components/area-gate";
import { ArrowRightIcon } from "../components/icons";
import { ALERTS_TABS, SectionTabs } from "../components/section-tabs";
import { EmptyState, TableSkeleton } from "../components/states";
import { ApiError, type BigClientAlert, resolveAlert } from "../lib/api";
import {
  budgetSortValue,
  formatBudget,
  HANDOFF_DESKS,
  handoffDeskLabel,
  meetingBookingState,
} from "../lib/big-clients";
import { formatDateTime } from "../lib/format";
import { AUTO_TAKEOVER_ACTOR } from "../lib/project-handling";
import { adminKeys, bigClientsQuery } from "../lib/queries";
import { projectTitle } from "../lib/project-title";
import { ExplorerProjectLink } from "../components/explorer-link";
import { WorkShapeTag } from "../components/work-shape-tag";
import { useDebounced } from "../lib/use-debounced";
import { WORK_SHAPES, workShapeLabel } from "../lib/work-shape";
import { useCountryFilterOptions } from "../components/country-filter";
import { UNKNOWN_COUNTRY } from "../lib/country";
import { DEVICE_FILTER_OPTIONS } from "../lib/device";
import "./big-clients.css";

function clientName(alert: BigClientAlert): string {
  return alert.ownerDisplayName || alert.ownerEmail || "Unknown client";
}

function alertProjectTitle(alert: BigClientAlert): string {
  return projectTitle({ id: alert.projectId, name: alert.projectName });
}

/** Who took this client over, as one line under the state badge.
 *
 *  The automatic engage writes a SENTINEL rather than a person
 *  (`web/takeover.py::AUTO_TAKEOVER_ACTOR`), and saying so is the whole point:
 *  "handled automatically" and "handled by Dana" mean opposite things to the
 *  operator deciding whether this client still needs somebody. An older backend
 *  carried the boolean alone, which reads as unrecorded rather than as nobody. */
function takeoverActor(alert: BigClientAlert): string {
  if (alert.humanTakeoverBy === AUTO_TAKEOVER_ACTOR) return "Automatic, on budget";
  return alert.humanTakeoverBy || "Operator not recorded";
}

const ALERT_SORT = {
  client: clientName,
  project: alertProjectTitle,
  budget: budgetSortValue,
  state: (a: BigClientAlert) => a.humanTakeover,
  // Two separate axes on purpose. The desk answers "whose client is this",
  // which is how the lane gets divided up; the meeting answers "did they turn
  // up", which is what makes a row actionable. Sorting one must not scramble
  // the other, so they are not folded into a single composite key.
  desk: (a: BigClientAlert) => handoffDeskLabel(a.handoffTrack) ?? "",
  meeting: (a: BigClientAlert) => meetingBookingState(a),
  raised: (a: BigClientAlert) => a.createdAt,
} as const;

/** The meeting cell: whether a routed client actually booked the desk's time.
 *
 *  Three states, not two. "Not booked" is the row an operator chases, so it must
 *  not look like the far more common "there was never a meeting to book" — a
 *  project the backend routed nowhere. */
function MeetingCell({ alert }: { alert: BigClientAlert }) {
  const state = meetingBookingState(alert);
  if (state === "none") return <span className="bc-no-desk">-</span>;
  return state === "booked" ? (
    <Badge tone="success" size="sm">
      Booked
    </Badge>
  ) : (
    <Badge tone="warning" size="sm">
      Not booked
    </Badge>
  );
}

/** The one repeating column on this desk: has a human already taken this client
 *  over, or is Mira still the only one on them? That is the question the lane is
 *  worked by ("show me the ones nobody has picked up"), and sorting the column
 *  only puts them in two clumps. (The takeover is a notification + CS handoff,
 *  not a stop — Mira keeps running either way, Monday 3148925054.)
 *
 *  A CLOSED vocabulary, because this console derives it from a boolean rather
 *  than reading it off the wire, and both halves must stay listed: "nobody has
 *  picked one up" has to read as an answer, not as a control that went missing.
 *  The selection travels to the SERVER (the `states` param), where it filters
 *  the whole desk rather than the loaded pages. */
const BIG_CLIENT_STATE_OPTIONS = [
  { value: "handled", label: "Human-handled" },
  { value: "mira", label: "Mira only" },
] as const;

/** The work-shape filter's fixed options: the three latched shapes plus the
 *  not-classified bucket (`none` is the value the server filter takes for it). */
const WORK_SHAPE_OPTIONS = [...WORK_SHAPES, "none"].map((value) => ({
  value,
  label: workShapeLabel(value),
}));

/** The desk filter: the three real desks plus the far larger "routed nowhere"
 *  bucket. A CLOSED vocabulary like the state pair beside it, not derived from
 *  the loaded rows — this lane is server-paged, so options read off the fetched
 *  pages would hide every desk that happens not to appear on page one. */
const HANDOFF_DESK_OPTIONS = [
  ...HANDOFF_DESKS.map((value) => ({ value, label: handoffDeskLabel(value) ?? value })),
  { value: "none", label: "No desk" },
];

/** The meeting filter. "Not booked" is the actionable one: a desk is waiting and
 *  the client has not turned up. It is kept apart from "No meeting" because most
 *  of this lane was never offered one, and folding the two together would bury
 *  the handful of rows worth chasing. */
const MEETING_OPTIONS = [
  { value: "booked", label: "Booked" },
  { value: "not_booked", label: "Not booked" },
  { value: "none", label: "No meeting" },
];

export function BigClientsPage() {
  const [filter, setFilter] = useRestoredState("big-clients/filter", "");
  const [states, setStates] = useRestoredState<readonly string[]>("big-clients/states", []);
  const [shapes, setShapes] = useRestoredState<readonly string[]>("big-clients/shapes", []);
  const [desks, setDesks] = useRestoredState<readonly string[]>("big-clients/desks", []);
  const [meetings, setMeetings] = useRestoredState<readonly string[]>("big-clients/meetings", []);
  // The project's creation stamps (device, origin country), the console-wide
  // filters, narrowing SERVER-side like the four above.
  const [devices, setDevices] = useRestoredState<readonly string[]>("big-clients/devices", []);
  const [countries, setCountries] = useRestoredState<readonly string[]>(
    "big-clients/countries",
    [],
  );
  const countryOptions = useCountryFilterOptions();
  // The cohort range. Local state like the filters beside it, and it narrows
  // SERVER-side for the same reason they do: this list is keyset-paged, so a
  // browser-side date filter could only ever cover the pages already fetched.
  const [range, setRange] = useRestoredState<DateRange>("big-clients/range", EMPTY_DATE_RANGE);
  // Debounced so a keystroke does not become a request: the text box narrows
  // SERVER-side, like the state filter.
  const q = useDebounced(filter.trim(), 300);
  const alerts = useInfiniteQuery(
    bigClientsQuery({ q, states, shapes, desks, meetings, devices, countries, range }),
  );
  const queryClient = useQueryClient();
  const toast = useToast();

  const dismiss = useMutation({
    mutationFn: (flagId: string) => resolveAlert(flagId),
    onSuccess: () => {
      toast.add({
        title: "Alert dismissed",
        description: "Automation is unchanged. Open the project to take it over.",
        data: { tone: "neutral" },
      });
      void queryClient.invalidateQueries({ queryKey: adminKeys.bigClients });
    },
    onError: (error: unknown) => {
      // 404 means another operator got there first: a race on a shared desk, not
      // a failure. Refresh so the row goes rather than leaving a dead button.
      if (error instanceof ApiError && error.status === 404) {
        toast.add({ title: "Already dismissed", data: { tone: "neutral" } });
        void queryClient.invalidateQueries({ queryKey: adminKeys.bigClients });
        return;
      }
      toast.add({
        title: "Couldn't dismiss that alert",
        description: error instanceof ApiError ? error.detail : undefined,
        data: { tone: "danger" },
      });
    },
  });

  // The state filter and the text box narrow SERVER-side, across the WHOLE
  // desk: it is keyset-paged, so a browser-side filter could only ever cover
  // the pages already fetched. The selection keys the query, and the rows
  // arriving here already match it.
  const all: BigClientAlert[] = useMemo(
    () => alerts.data?.pages.flatMap((p) => p.alerts) ?? [],
    [alerts.data],
  );
  const columns = useMemo<readonly FilterColumn<BigClientAlert>[]>(
    () => [
      {
        key: "state",
        label: "Filter by state",
        allLabel: "Any state",
        value: (a) => (a.humanTakeover ? "handled" : "mira"),
        options: BIG_CLIENT_STATE_OPTIONS,
        selected: states,
        onChange: setStates,
      },
      {
        key: "workShape",
        label: "Filter by work shape",
        allLabel: "All work shapes",
        // Null IS "none" server-side (the not-classified bucket), so the local
        // pass over the loaded rows agrees with the SQL narrowing.
        value: (a) => a.workShape ?? "none",
        options: WORK_SHAPE_OPTIONS,
        selected: shapes,
        onChange: setShapes,
      },
      {
        key: "handoffDesk",
        label: "Filter by desk",
        allLabel: "All desks",
        // No desk IS "none" server-side, and an unrecognised one files there
        // too, so the local pass agrees with the SQL narrowing.
        value: (a) =>
          (handoffDeskLabel(a.handoffTrack) === null ? "none" : a.handoffTrack) ?? "none",
        options: HANDOFF_DESK_OPTIONS,
        selected: desks,
        onChange: setDesks,
      },
      {
        key: "handoffMeeting",
        label: "Filter by meeting",
        allLabel: "All meetings",
        value: (a) => meetingBookingState(a),
        options: MEETING_OPTIONS,
        selected: meetings,
        onChange: setMeetings,
      },
      {
        key: "device",
        label: "Filter by device",
        allLabel: "All devices",
        // Null IS "unknown" server-side (no stamp recorded), so the local pass
        // over the loaded rows agrees with the SQL narrowing.
        value: (a) => a.createdDevice ?? "unknown",
        options: DEVICE_FILTER_OPTIONS,
        selected: devices,
        onChange: setDevices,
      },
      {
        key: "country",
        label: "Filter by country",
        allLabel: "All countries",
        value: (a) => a.originCountry ?? UNKNOWN_COUNTRY,
        options: countryOptions,
        selected: countries,
        onChange: setCountries,
      },
    ],
    [
      states,
      shapes,
      desks,
      meetings,
      devices,
      countries,
      countryOptions,
      setStates,
      setShapes,
      setDesks,
      setMeetings,
      setDevices,
      setCountries,
    ],
  );
  const filters = useColumnFilters(all, columns);
  const rows = filters.rows;
  const sorted = useTableSort(rows, ALERT_SORT, null, { restore: "big-clients/sort" });
  const narrowed = filter.trim() !== "" || filters.active || isDateRangeActive(range);

  if (alerts.isError) {
    return (
      <div className="ad-page">
        <AreaBoundary area="alerts" error={alerts.error} onRetry={() => void alerts.refetch()} />
      </div>
    );
  }

  return (
    <div className="ad-page">
      <header className="ad-page__head">
        <h1 className="ad-page__title">Big clients</h1>
        <p className="ad-page__sub">
          Projects whose committed budget crossed the big-client threshold. Open one to read the
          conversation, check the brief and the matches, and take it over personally.
        </p>
      </header>

      <SectionTabs label="Alerts sections" tabs={ALERTS_TABS} />

      <div className="bc-toolbar">
        <Input
          type="search"
          inputSize="sm"
          placeholder="Filter by client or project"
          aria-label="Filter big clients"
          value={filter}
          onChange={(event) => setFilter(event.target.value)}
        />
        <DateRangeFilter label="Raised" value={range} onChange={setRange} />
        <TableFilters
          controls={filters.controls}
          active={narrowed}
          onClear={() => {
            setFilter("");
            filters.clear();
            setRange(EMPTY_DATE_RANGE);
          }}
        />
        {/* "loaded", not "open": the desk is keyset-paged, so this counts what
            has been fetched, not how many big clients are waiting. The filters
            cover the whole desk (server-side), so every loaded row matches. */}
        {alerts.data ? (
          <span className="rc-toolbar__count">
            {rows.length.toLocaleString()} of {all.length.toLocaleString()} loaded
            {alerts.hasNextPage ? ", more available" : ""}
          </span>
        ) : null}
        <Button
          variant="outline"
          tone="neutral"
          size="sm"
          disabled={alerts.isFetching}
          onClick={() => void alerts.refetch()}
        >
          {alerts.isFetching ? "Refreshing" : "Refresh"}
        </Button>
      </div>

      {alerts.isPending ? (
        <TableSkeleton columns={8} />
      ) : all.length === 0 && !narrowed ? (
        <EmptyState
          title="No open big-client alerts"
          message="One appears the moment a client commits a budget at or above the threshold."
        />
      ) : rows.length === 0 ? (
        <EmptyState
          title={filter ? `No big clients match “${filter}”` : "No big clients match those filters"}
          message="The whole desk was searched. Try clearing a filter."
        />
      ) : (
        <>
          <div className="rc-table-wrap">
            <table className="rc-table">
              <TableExport name="big-clients" label="big clients" />
              <thead>
                <tr>
                  <SortableTh sortKey="client" ariaSort={sorted.ariaSort} onToggle={sorted.toggle}>
                    Client
                  </SortableTh>
                  <SortableTh sortKey="project" ariaSort={sorted.ariaSort} onToggle={sorted.toggle}>
                    Project
                  </SortableTh>
                  <SortableTh sortKey="budget" ariaSort={sorted.ariaSort} onToggle={sorted.toggle}>
                    Budget
                  </SortableTh>
                  <SortableTh sortKey="state" ariaSort={sorted.ariaSort} onToggle={sorted.toggle}>
                    State
                  </SortableTh>
                  {/* Where the handoff was routed, and whether the client
                      booked it. Written on the project since the handoff
                      shipped and shown to nobody until now (Monday
                      3168800870). */}
                  <SortableTh sortKey="desk" ariaSort={sorted.ariaSort} onToggle={sorted.toggle}>
                    Desk
                  </SortableTh>
                  <SortableTh sortKey="meeting" ariaSort={sorted.ariaSort} onToggle={sorted.toggle}>
                    Meeting
                  </SortableTh>
                  <SortableTh sortKey="raised" ariaSort={sorted.ariaSort} onToggle={sorted.toggle}>
                    Raised
                  </SortableTh>
                  <th className="rc-actions-col">Actions</th>
                </tr>
              </thead>
              <tbody>
                {sorted.rows.map((alert) => (
                  <tr key={alert.id}>
                    <td>{clientName(alert)}</td>
                    <td>
                      <span className="ex-title-line">
                        <ExplorerProjectLink
                          projectId={alert.projectId}
                          className="bc-project-link"
                          denied={<span>{alertProjectTitle(alert)}</span>}
                        >
                          {alertProjectTitle(alert)}
                          <ArrowRightIcon size={14} />
                        </ExplorerProjectLink>
                        {/* The engagement shape: an ongoing big client is a
                            different conversation than a one-off. */}
                        <WorkShapeTag shape={alert.workShape} />
                      </span>
                    </td>
                    <td className="bc-budget" title={alert.detail ?? undefined}>
                      {/* A rate prints in the client's own unit ("$1,500/mo") —
                          the commitment that actually paged the desk. */}
                      {formatBudget(alert)}
                    </td>
                    <td>
                      {alert.humanTakeover ? (
                        <>
                          <Badge tone="warning" size="sm">
                            Human-handled
                          </Badge>
                          {/* WHO has them. The lane is worked by "is anybody on
                            this yet", and the answer "somebody" is only half of
                            it — the other half decides whether an operator
                            opens the project or moves on. */}
                          <span className="bc-handled-by">{takeoverActor(alert)}</span>
                        </>
                      ) : (
                        <Badge tone="neutral" size="sm">
                          Mira only
                        </Badge>
                      )}
                    </td>
                    <td>
                      {/* The desk name alone. A dash rather than "None" because
                          most projects route nowhere, and the column should
                          read as empty for them, not as a decision. */}
                      {handoffDeskLabel(alert.handoffTrack) ?? (
                        <span className="bc-no-desk">-</span>
                      )}
                    </td>
                    <td>
                      <MeetingCell alert={alert} />
                    </td>
                    <td>{formatDateTime(alert.createdAt)}</td>
                    <td className="bc-actions">
                      <ExplorerProjectLink
                        projectId={alert.projectId}
                        className="bc-open"
                        denied={null}
                      >
                        Open project
                      </ExplorerProjectLink>
                      <Button
                        variant="ghost"
                        tone="neutral"
                        size="sm"
                        disabled={dismiss.isPending && dismiss.variables === alert.id}
                        title="Take the alert off this list. Automation is not changed."
                        onClick={() => dismiss.mutate(alert.id)}
                      >
                        Dismiss
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {alerts.hasNextPage ? (
            <div className="ex-load-more">
              <Button
                type="button"
                variant="outline"
                tone="neutral"
                size="sm"
                disabled={alerts.isFetchingNextPage}
                onClick={() => void alerts.fetchNextPage()}
              >
                {alerts.isFetchingNextPage ? "Loading…" : "Load more big clients"}
              </Button>
            </div>
          ) : null}
        </>
      )}
    </div>
  );
}
